import React, { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { getToken } from "@/lib/auth";

const EVENT_LABELS = {
  home_view:          "Home View",
  cta_start_clicked:  "Start Journey CTA",
  login_clicked:      "Login Clicked",
  logout_clicked:     "Logout Clicked",
  onboarding_started: "Onboarding Started",
  app_banner_clicked: "App Banner Clicked",
};

export default function AdminAnalytics() {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");
  const [filter, setFilter]           = useState("");
  const [deleteMsg, setDeleteMsg]     = useState("");
  const [excludeBots, setExcludeBots] = useState(true);
  const [growthData, setGrowthData] = useState(null);

  const loadGrowth = () => {
    const token = getToken();
    fetch('/api/admin/growth-metrics', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => setGrowthData(d))
      .catch(() => {});
  };

  const loadData = (excl = excludeBots) => {
    const token = getToken();
    setLoading(true);
    fetch(`/api/admin/analytics/events?exclude_bots=${excl}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setError("Failed to load analytics: " + e); setLoading(false); });
  };

  useEffect(() => { loadData(excludeBots); loadGrowth(); }, [excludeBots]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteAnalytics = (body, label) => {
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    const token = getToken();
    setDeleteMsg("");
    fetch("/api/admin/analytics/events", {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { setDeleteMsg(`Deleted ${d.deleted_count} event(s) [${d.mode}].`); loadData(); })
      .catch((e) => setDeleteMsg("Delete failed: " + e));
  };

  const deleteRow = (id) => {
    const token = getToken();
    fetch(`/api/admin/analytics/event/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(() => loadData())
      .catch((e) => setDeleteMsg("Row delete failed: " + e));
  };

  const counts  = data?.counts  || {};
  const events  = data?.events  || [];

  const filtered = events.filter((e) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (e.event  || "").toLowerCase().includes(q) ||
      (e.page   || "").toLowerCase().includes(q) ||
      (e.city   || "").toLowerCase().includes(q) ||
      (e.state  || "").toLowerCase().includes(q)
    );
  });

  return (
    <AdminShell>
      <div className="p-6 md:p-10 text-white space-y-8">
        <div>
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-2">Admin</div>
          <h1 className="font-display text-3xl">Analytics</h1>

        {growthData && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 mb-6">

            <div className="glass rounded-xl p-4">
              <div className="text-white/40 text-xs">Total Users</div>
              <div className="text-2xl font-bold">{growthData.total_users}</div>
            </div>

            <div className="glass rounded-xl p-4">
              <div className="text-white/40 text-xs">Conversion</div>
              <div className="text-2xl font-bold">{growthData.conversion_rate}%</div>
            </div>

            <div className="glass rounded-xl p-4">
              <div className="text-white/40 text-xs">Referrals</div>
              <div className="text-2xl font-bold">{growthData.referrals}</div>
            </div>

            <div className="glass rounded-xl p-4">
              <div className="text-white/40 text-xs">Top Source</div>
              <div className="text-lg font-bold">
                {growthData.sources?.[0]?._id || "—"}
              </div>
            </div>

          </div>
        )}
        </div>

        {data?.onboarding_funnel && (
          <div className="glass rounded-2xl p-5 space-y-3">
            <div className="font-display text-lg">Onboarding Performance</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="rounded-xl bg-white/[0.03] border border-white/8 p-4">
                <div className="text-white/40 text-xs mb-1">Started</div>
                <div className="text-2xl font-bold">{data.onboarding_funnel.start ?? "—"}</div>
              </div>
              <div className="rounded-xl bg-white/[0.03] border border-white/8 p-4">
                <div className="text-white/40 text-xs mb-1">Continued</div>
                <div className="text-2xl font-bold">{data.onboarding_funnel.continue ?? "—"}</div>
                <div className="text-xs text-white/30 mt-1">
                  {data.onboarding_funnel.conversion?.continue_rate != null
                    ? `${(data.onboarding_funnel.conversion.continue_rate * 100).toFixed(1)}%`
                    : ""}
                </div>
              </div>
              <div className="rounded-xl bg-white/[0.03] border border-white/8 p-4">
                <div className="text-white/40 text-xs mb-1">Completed</div>
                <div className="text-2xl font-bold">{data.onboarding_funnel.complete ?? "—"}</div>
                <div className="text-xs text-white/30 mt-1">
                  {data.onboarding_funnel.conversion?.completion_rate != null
                    ? `${(data.onboarding_funnel.conversion.completion_rate * 100).toFixed(1)}%`
                    : ""}
                </div>
              </div>
              <div className="rounded-xl bg-white/[0.03] border border-white/8 p-4">
                <div className="text-white/40 text-xs mb-1">Blocked</div>
                <div className="text-2xl font-bold text-orange-400">{data.onboarding_funnel.blocked ?? "—"}</div>
                <div className="text-xs text-white/30 mt-1">
                  {data.onboarding_funnel.conversion?.dropoff_rate != null
                    ? `${(data.onboarding_funnel.conversion.dropoff_rate * 100).toFixed(1)}%`
                    : ""}
                </div>
              </div>
            </div>
          </div>
        )}

        {data?.funnel && (
          <div className="glass rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-display text-lg">Step Funnel</div>
              <div className="text-xs text-white/40">Compact</div>
            </div>
            <div className="grid grid-cols-6 gap-2">
              {[
                { label: "Start", key: "started" },
                { label: "S0", key: "step_0" },
                { label: "S1", key: "step_1" },
                { label: "S2", key: "step_2" },
                { label: "S3", key: "step_3" },
                { label: "Done", key: "completed" },
              ].map((step) => {
                const max = Math.max(data.funnel.started || 1, 1);
                const count = data.funnel[step.key] || 0;
                const pct = Math.max(6, Math.round((count / max) * 100));
                return (
                  <div key={step.key} className="space-y-2">
                    <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#D4AF37]"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-white/50">{step.label}</span>
                      <span className="text-xs font-semibold text-white">{count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="glass rounded-2xl p-5 space-y-3">
          <div className="font-display text-lg">Manage Analytics Data</div>

          {deleteMsg && (
            <div className="text-sm px-3 py-2 rounded-lg bg-white/5 text-[#F2DE94]">{deleteMsg}</div>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => deleteAnalytics({ mode: "anonymous" }, "all anonymous events")}
              className="text-xs px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/5 transition"
            >
              Clear Anonymous
            </button>
            <button
              onClick={() => deleteAnalytics({ mode: "time", hours: 1 }, "events from the last 1 hour")}
              className="text-xs px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/5 transition"
            >
              Delete Last 1h
            </button>
            <button
              onClick={() => deleteAnalytics({ mode: "time", hours: 24 }, "events from the last 24 hours")}
              className="text-xs px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/5 transition"
            >
              Delete Last 24h
            </button>
            <button
              onClick={() => deleteAnalytics({ mode: "time", hours: 168 }, "events from the last 7 days")}
              className="text-xs px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/5 transition"
            >
              Delete Last 7d
            </button>
            <button
              onClick={() => deleteAnalytics({ mode: "time", hours: 720 }, "events from the last 30 days")}
              className="text-xs px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/5 transition"
            >
              Delete Last 30d
            </button>
            <button
              onClick={() => deleteAnalytics({ mode: "bot" }, "all bot events")}
              className="text-xs px-3 py-1.5 rounded-lg border border-orange-500/30 text-orange-400 hover:bg-orange-500/10 transition"
            >
              Delete All Bots
            </button>
            <button
              onClick={() => deleteAnalytics({ mode: "all" }, "ALL analytics events")}
              className="text-xs px-3 py-1.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition ml-auto"
            >
              Clear All Events
            </button>
          </div>
          {Object.keys(data?.counts || {}).length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1 border-t border-white/5">
              <span className="text-white/40 text-xs self-center">Clear by type:</span>
              {Object.keys(data.counts).map((evt) => (
                <button
                  key={evt}
                  onClick={() => deleteAnalytics({ mode: "event", event: evt }, `all "${evt}" events`)}
                  className="text-xs px-2.5 py-1 rounded-lg bg-[rgba(212,175,55,0.07)] text-[#F2DE94] hover:bg-[rgba(212,175,55,0.15)] transition"
                >
                  {evt}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* EVENT LOG */}
        <div>
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            <h2 className="font-display text-xl">Event Log</h2>
            <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={excludeBots}
                onChange={(e) => setExcludeBots(e.target.checked)}
                className="accent-[#D4AF37]"
              />
              Exclude bots
            </label>
            <input
              type="text"
              placeholder="Filter by event, page, city, state…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="ml-auto w-full max-w-xs px-4 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder:text-white/30 outline-none focus:border-[rgba(212,175,55,0.4)] text-sm"
            />
            <span className="text-white/35 text-sm whitespace-nowrap shrink-0">{filtered.length} rows</span>
          </div>

          {loading && <div className="text-white/40">Loading…</div>}
          {error   && <div className="text-red-400">{error}</div>}

          {!loading && !error && (
            <div className="overflow-x-auto rounded-2xl border border-white/8">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/40 text-[11px] uppercase tracking-wider border-b border-white/8">
                    {["Event", "Type", "Page", "City", "State", "User ID", "Time", ""].map((h) => (
                      <th key={h} className="px-4 py-3 text-left font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e, i) => (
                    <tr key={e.id || i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-2.5 font-medium whitespace-nowrap">
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-[rgba(212,175,55,0.1)] text-[#F2DE94]">
                          {EVENT_LABELS[e.event] || e.event}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        {e.traffic_type === "bot" ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(239,68,68,0.12)] text-red-400">bot</span>
                        ) : e.traffic_type === "anon" ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-white/40">anon</span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(52,211,153,0.1)] text-emerald-400">auth</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-white/55 font-mono text-xs">{e.page || "—"}</td>
                      <td className="px-4 py-2.5 text-white/55">{e.city  || "—"}</td>
                      <td className="px-4 py-2.5 text-white/55">{e.state || "—"}</td>
                      <td className="px-4 py-2.5 text-white/35 font-mono text-xs truncate max-w-[120px]">
                        {e.user_id || "anon"}
                      </td>
                      <td className="px-4 py-2.5 text-white/35 text-xs whitespace-nowrap">
                        {e.created_at ? e.created_at.replace("T", " ").slice(0, 19) : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          onClick={() => deleteRow(e.id)}
                          title="Delete this event"
                          className="text-white/20 hover:text-red-400 transition-colors text-xs px-1"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-white/30">
                        No events found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
